import * as ClientUtils from '../../../../../../../lib/ClientInfoUtils';
import EventCoordinator from '../../../../../../../tools/EventCoordinator';
import * as PlaybackUtils from '../../../../../utils';
import * as AudioConstants from '../../../../constants';
import * as D from '../../../../deps';
import * as AudioUtils from '../../../../utils';

import swf from './web-audio-player.swf';

const log = D.Logger.child({ component: 'harnesses/SXMHarness' });

export const NAME = AudioConstants.AudioHarnessName.SXM_AUDIO_PLAYER;
const PLAYBACK_TYPE_MP3 = 'playbackType.MP3';
const PLAYBACK_TYPE_MP4 = 'playbackType.MP4';
const VOLUME_MULTIPLIER = 0.01;
const DURATION_MULTIPLIER = 1000; // converting between s and ms
const DEFAULT_CHUNKS_PER_PULL = 40;
const DEFAULT_MP4_HEADER_SIZE = 400000;
const DEFAULT_FAILOVER_RETRY_LIMIT = 0;
const INITIAL_PLAYER_STATE = {
  current: null,
  id: null,
  previous: null,
  type: null,
};

const eventer = new EventCoordinator();

let SXMAudioPlayer = null;
const playerState = Object.assign({}, INITIAL_PLAYER_STATE);
const playheadEvent = {};
let isTrackChangeByClient = false;

const SXMAudioPlayerEvents = Object.freeze({
  PLAYHEAD_START: 'playheadStart',
  PLAYHEAD: 'playhead',
  STATE_CHANGE: 'playStateChanged',
  FINISHED: 'finished',
  TOKEN_INVALID: 'tokenInvalid',
  NOT_ALLOWED_ERROR: 'NotAllowedError',
  ERROR: 'error',
  TRACK_FAILED: 'trackFailed',
  ASSET_ERROR: 'AssetError',
  PLAYBACK_METRICS: 'playbackMetrics',
});

const ERROR_MAPPING = {
  [SXMAudioPlayerEvents.TOKEN_INVALID]: {
    message: 'Current HTTP token is invalid and the client needs to grab another for the player',
    commonType: AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED,
  },
  [SXMAudioPlayerEvents.NOT_ALLOWED_ERROR]: {
    message: 'Browser autoplay blocked',
    commonType: AudioConstants.CommonAudioErrorCodes.BROWSER_AUTOPLAY_BLOCKED,
  },
  [SXMAudioPlayerEvents.ASSET_ERROR]: {
    message: 'Asset endpoint responded with HTTP 500 Internal Server Error',
    commonType: AudioConstants.CommonAudioErrorCodes.SERVICE_ERROR,
  },
  [SXMAudioPlayerEvents.TRACK_FAILED]: {
    message: 'Unrecognized errors occur other than timeout and 403',
    commonType: AudioConstants.CommonAudioErrorCodes.SERVICE_ERROR,
  },
};

const createQueryablePromise =
  request =>
  (...params) => {
    if (request.getStatus) {
      return request;
    }

    let status = 'pending';
    const queryablePromise = request(...params)
      .then(v => {
        status = 'resolved';
        return v;
      })
      .catch(e => {
        status = 'rejected';
        throw e;
      });

    queryablePromise.getStatus = () => status;
    return queryablePromise;
  };

const createOrReturnExistingQueryablePromise = (
  request,
  requestName,
  requestPromises,
  ...params
) => {
  const pendingRequest = requestPromises[requestName];
  if (!pendingRequest || (pendingRequest && pendingRequest.getStatus() !== 'pending')) {
    const queryablePromise = createQueryablePromise(request)(...params);
    requestPromises[requestName] = queryablePromise;
    return queryablePromise;
  }
  return pendingRequest;
};

const isUsingMediaElement = (audioSrc, options) => {
  const state = D.getStore().getState();
  const trackData = AudioUtils.getTrackDataForLogging(state);
  return (
    (!audioSrc.key &&
      (AudioConstants.sxmMediaelementAudioTypes.includes(audioSrc.audioType) ||
        options.audioData.isSxmUnencryptedMediaelementPlayback2021Enabled)) ||
    (audioSrc.key &&
      trackData.trackType === 'STATION' &&
      options.audioData.isSxmEncryptedStationFragmentedEnabled)
  );
};

const getPlaybackType = audioSrc => {
  if (audioSrc.key) {
    return PLAYBACK_TYPE_MP4; // MP4 is the only format that supports encryption.
  }
  return audioSrc.format === AudioConstants.AudioEncodingFormats.MP3
    ? PLAYBACK_TYPE_MP3
    : PLAYBACK_TYPE_MP4;
};

const createAudioConfig = (audioSrc, options) => {
  const useMediaElement = isUsingMediaElement(audioSrc, options);
  return {
    id: audioSrc.url,
    key: audioSrc.key,
    playbackType: getPlaybackType(audioSrc),
    useMediaElement,
    autoPlay: true,
    withCredentials: false,
    chunksPerPull: DEFAULT_CHUNKS_PER_PULL,
    mp4HeaderSize: DEFAULT_MP4_HEADER_SIZE,
    FAILOVER_RETRY_LIMIT: DEFAULT_FAILOVER_RETRY_LIMIT,
    audioURLs: [
      {
        small: audioSrc.url,
      },
    ],
  };
};

export function initialize(audioSrc, resolve, reject, options = {}) {
  let filePath = './SXMAudioPlayer.js';
  if (window.fetch && !ClientUtils.isElectron()) {
    filePath = './SXMAudioPlayer_fetchDownloader_v2.js';
  }

  function onAudioPlayerLoad(sxmAudioPlayer) {
    if (!SXMAudioPlayer) {
      SXMAudioPlayer = new sxmAudioPlayer.default({
        SWF_FILE: swf.split('/').pop().slice(0, -4),
        PATH: ASSET_LOCATION,
        logger: D.Logger.child({ component: 'sxm-audio-player' }),
        enableLogging: true,
        debugMode: true,
      });
    }

    if (options.isPreload) {
      SXMAudioPlayer.preload(audioSrc.url, createAudioConfig(audioSrc));
    }

    const sxmHarnessType = isUsingMediaElement(audioSrc, options)
      ? AudioConstants.AudioHarnessType.SXM_MEDIA_ELEMENT
      : AudioConstants.AudioHarnessType.SXM_WEB_AUDIO_API;

    resolve({
      sxmAudioPlayer: SXMAudioPlayer,
      sxmHarnessType,
      audioSrcObj: audioSrc,
      eventHandlers: {},
      requestPromises: {},
      volumeSettings: {
        isMuted: false,
        volume: 0,
      },
    });
  }

  const state = D.getStore().getState();
  if (D.Utils.TunerVars.audioPlayerSDKEnabled(state)) {
    return System.import(/* webpackChunkName: "sxm-audio-player" */ '@sxmp/audio-player')
      .then(onAudioPlayerLoad)
      .catch(e => {
        reject(e);
      });
  }
  return System.import(/* webpackChunkName: "sxm-audio-player" */ `${filePath}`)
    .then(onAudioPlayerLoad)
    .catch(e => {
      reject(e);
    });
}

export function mapMethods(
  { sxmAudioPlayer, sxmHarnessType, audioSrcObj, requestPromises, volumeSettings, eventHandlers },
  options,
) {
  function onPlayRejected(e) {
    if (
      e.url === audioSrcObj.url &&
      e &&
      e.name === SXMAudioPlayerEvents.ASSET_ERROR &&
      e.httpStatus === 500
    ) {
      Object.assign(playerState, INITIAL_PLAYER_STATE);
      eventHandlers.onError(
        new Error(JSON.stringify(ERROR_MAPPING[SXMAudioPlayerEvents.ASSET_ERROR])),
      );
    }
  }
  return {
    play(startTime) {
      // when playing a preloaded track, sxm player will use the configs from the original preload() call
      const config = createAudioConfig(audioSrcObj, options);
      if (
        playerState.id === audioSrcObj.url &&
        (playerState.current === 'paused' || playerState.current === 'seeking')
      ) {
        createOrReturnExistingQueryablePromise(
          sxmAudioPlayer.resume.bind(sxmAudioPlayer),
          'play',
          requestPromises,
        );
      } else if (playerState.id !== audioSrcObj.url || playerState.current !== 'playing') {
        const pos = startTime * DURATION_MULTIPLIER;
        const playConfig = pos > 0 ? { startTime: pos, ...config } : config;
        isTrackChangeByClient = true;
        createOrReturnExistingQueryablePromise(
          sxmAudioPlayer.play.bind(sxmAudioPlayer),
          'play',
          requestPromises,
          audioSrcObj.url,
          playConfig,
        ).catch(onPlayRejected);
      }
    },

    pause() {
      createOrReturnExistingQueryablePromise(
        sxmAudioPlayer.pause.bind(sxmAudioPlayer),
        'pause',
        requestPromises,
      );
    },

    setVolume(volume) {
      volumeSettings.volume = volume;
      if (!volumeSettings.isMuted) {
        sxmAudioPlayer.setVolume(volume * VOLUME_MULTIPLIER);
      }
    },

    setCurrentTime(suggestedPos, fromPlayClick) {
      if (!fromPlayClick) {
        const pos = suggestedPos * DURATION_MULTIPLIER;
        if (playerState.id === audioSrcObj.url && pos !== playheadEvent.playhead) {
          createOrReturnExistingQueryablePromise(
            sxmAudioPlayer.seek.bind(sxmAudioPlayer),
            'seek',
            requestPromises,
            pos,
          );
        }
      }
    },

    setMuted(isMuted) {
      volumeSettings.isMuted = isMuted;
      if (isMuted && sxmAudioPlayer.getVolume() > 0) {
        sxmAudioPlayer.setVolume(0);
      } else if (!isMuted && sxmAudioPlayer.getVolume() === 0) {
        sxmAudioPlayer.setVolume(volumeSettings.volume * VOLUME_MULTIPLIER);
      }
    },

    updateUrl(audioUrl) {
      createOrReturnExistingQueryablePromise(
        sxmAudioPlayer.updatePlaybackSource.bind(sxmAudioPlayer),
        'updateUrl',
        requestPromises,
        audioUrl,
        audioSrcObj.url,
      ).catch(error => {
        log.warn('Failed to updateUrl', error);
        throw error;
      });
    },

    destroy() {},

    getState() {
      return {
        readyState: null,
        currentTime: playheadEvent.playhead / DURATION_MULTIPLIER,
        buffered: playheadEvent.loaded / DURATION_MULTIPLIER,
        bufferedRemaining: playheadEvent.buffered / DURATION_MULTIPLIER,
        duration: playheadEvent.duration / DURATION_MULTIPLIER,
        ended: null,
        error: null,
        muted: sxmAudioPlayer.isMuted(),
        paused: playerState.current === 'paused',
        played: null,
        src: null,
        isPreload: options.isPreload,
        playbackMetrics: playheadEvent.playbackMetrics,
        trackMetadata: playheadEvent.trackMetadata,
        sxmHarnessType,
      };
    },
  };
}

export function mapEventBindings({ sxmAudioPlayer, eventHandlers, audioSrcObj }) {
  function onPlayhead(event) {
    if (
      playheadEvent &&
      playheadEvent.id !== event.id &&
      playheadEvent.duration - playheadEvent.playhead > 2000
    ) {
      if (!isTrackChangeByClient) {
        log.error(
          'Failed to Completely play the track:-',
          playheadEvent.id,
          '\n Switched at:-',
          playheadEvent.playhead,
          ' \n to',
          event.id,
        );
      } else {
        log.debug(
          'Switched to New track from Client action, Old track:-',
          playheadEvent.id,
          ' \n New track:-',
          event.id,
        );
      }
    }

    if (playheadEvent?.id !== event.id) {
      const state = D.getStore().getState();
      const trackData = AudioUtils.getTrackDataForLogging(state);
      const isAPS = PlaybackUtils.isPlaylistModelTypeAPSItem(state);
      playheadEvent.trackMetadata = { ...trackData, isAPS };
    }

    if (event.id === audioSrcObj.url) {
      Object.assign(playheadEvent, event);
      eventHandlers.onPlayhead && eventHandlers.onPlayhead(event);
      isTrackChangeByClient = false;
    }
  }

  function onStateChange(event) {
    if (event.id === audioSrcObj.url) {
      Object.assign(playerState, event);
      switch (event.current) {
        case 'paused':
          eventHandlers.onPause && eventHandlers.onPause(event);
          break;
        case 'playing':
          eventHandlers.onPlaying && eventHandlers.onPlaying(event);
          break;
      }
    }
  }

  function onError(event) {
    const error = ERROR_MAPPING[event && event.type] || {
      message: `An unknown media error occurred ${event.type}`,
      commonType: AudioConstants.CommonAudioErrorCodes.UNKNOWN,
    };
    if (
      error.commonType === AudioConstants.CommonAudioErrorCodes.SRC_NOT_SUPPORTED &&
      event.id !== audioSrcObj.url
    ) {
      return;
    }
    if (
      error.commonType === AudioConstants.CommonAudioErrorCodes.BROWSER_AUTOPLAY_BLOCKED &&
      event.id === audioSrcObj.url
    ) {
      onPlayhead(event);
    }
    eventHandlers.onError(new Error(JSON.stringify(error)));
  }

  function onEnded(event) {
    if (event.id === audioSrcObj.url) {
      eventHandlers.onEnded(event);
    }
  }

  function onPlaybackMetrics(event) {
    playheadEvent.playbackMetrics = event;
    eventHandlers.onPlaybackMetrics(event);
  }

  return {
    play(handlerFn) {},

    pause(handlerFn) {
      eventHandlers.onPause = handlerFn;
    },

    playing(handlerFn) {
      // playheadStart only fires on initial play start. This is a hack to call the handlerFn outside of this context
      // so that we can call the handlerFn every time the player state gets set to playing.
      eventHandlers.onPlaying = handlerFn;
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.STATE_CHANGE, onStateChange);
    },

    stalled(handlerFn) {},

    timeupdate(handlerFn) {
      eventHandlers.onPlayhead = handlerFn;
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.PLAYHEAD, onPlayhead);
    },

    durationchange(handlerFn) {},

    loadedmetadata(handlerFn) {},

    canplaythrough(handlerFn) {},

    progress(handlerFn) {},

    ended(handlerFn) {
      eventHandlers.onEnded = handlerFn;
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.FINISHED, onEnded);
    },

    seeked(handlerFn) {},

    error(handlerFn) {
      eventHandlers.onError = handlerFn;
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.ERROR, onError);
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.TOKEN_INVALID, onError);
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.NOT_ALLOWED_ERROR, onError);
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.TRACK_FAILED, onError);
    },

    playbackMetrics(handlerFn) {
      eventHandlers.onPlaybackMetrics = handlerFn;
      eventer.onOnly(sxmAudioPlayer, SXMAudioPlayerEvents.PLAYBACK_METRICS, onPlaybackMetrics);
    },
  };
}

export function mapEventRemovers({ sxmAudioPlayer, eventHandlers }) {
  return {
    play(handlerFn) {},

    pause(handlerFn) {
      eventHandlers.onPause = null;
    },

    playing(handlerFn) {
      eventHandlers.onPlaying = null;
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.STATE_CHANGE);
    },

    stalled(handlerFn) {},

    timeupdate(handlerFn) {
      eventHandlers.onPlayhead = null;
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.PLAYHEAD);
    },

    durationchange(handlerFn) {},

    loadedmetadata(handlerFn) {},

    canplaythrough(handlerFn) {},

    progress(handlerFn) {},

    ended(handlerFn) {
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.FINISHED);
    },

    seeked(handlerFn) {},

    error(handlerFn) {
      eventHandlers.onError = null;
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.ERROR);
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.TOKEN_INVALID);
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.NOT_ALLOWED_ERROR);
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.TRACK_FAILED);
    },

    playbackMetrics(handlerFn) {
      eventHandlers.onPlaybackMetrics = null;
      eventer.offOnly(sxmAudioPlayer, SXMAudioPlayerEvents.PLAYBACK_METRICS);
    },
  };
}
